// Try it (TOM-868, cloud ADR 0419): the home page's link leads here. The page
// asks `POST /v1/demo/try` for a demo boat at once (api_contracts.md §2.8),
// reads the new guest session, and opens the boat's home. There is no browser
// check: rate limits and the slot count guard it (Mike, 2026-10-27).
//
// A visitor whose demo copy ended arrives here with `state.ended`. The page then
// says so and waits for a click before it leases a new boat, so a tab left open
// does not take a demo boat by itself.
//
// The request is sent once per attempt. React runs effects twice in
// development, and two requests without a session would lease two boats.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';
import { boatPath } from '../components/app/shellNav';
import {
  browserTimeZone,
  DEMO_ENDED_NOTE,
  isDemoGuest,
  markDemoGuest,
  tryItFailure,
  type TryItFailure,
} from '../lib/demo';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';

interface TryResponse {
  boatId: string;
  expiresAt: string;
}

type Phase = { kind: 'ended' } | { kind: 'starting' } | { kind: 'failed'; failure: TryItFailure };

export function TryIt() {
  useDocumentTitle('Try it - Neomaris');
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ended = (location.state as { ended?: boolean } | null)?.ended === true;
  const { loaded, error: configError, reload: reloadConfig } = useClientConfig();

  const [phase, setPhase] = useState<Phase>(ended ? { kind: 'ended' } : { kind: 'starting' });
  // Bumped for each new attempt.
  const [attempt, setAttempt] = useState(0);
  // The attempt whose request has been sent.
  const sentFor = useRef<number | null>(null);

  // The visitor's earlier demo copy is gone. Forget it, so a later 401 is not
  // read as another ended lease.
  useEffect(() => {
    if (ended) markDemoGuest(false);
  }, [ended]);

  const { refresh } = auth;
  const start = useCallback(
    async (forAttempt: number) => {
      if (sentFor.current === forAttempt) return;
      sentFor.current = forAttempt;
      try {
        // The browser's zone: the demo boat plays on the visitor's own clock (TOM-917).
        const timeZone = browserTimeZone();
        const res = await api<TryResponse>('/v1/demo/try', {
          method: 'POST',
          body: timeZone === undefined ? {} : { timeZone },
        });
        markDemoGuest(true);
        // The session probe ran before the guest session existed. Read it
        // again, or the boat's pages would send the visitor to sign-in.
        await refresh();
        navigate(boatPath(res.boatId, ''), { replace: true });
      } catch (err) {
        setPhase({ kind: 'failed', failure: tryItFailure(err) });
      }
    },
    [navigate, refresh]
  );

  const ready = loaded && !configError && auth.status !== 'unknown';
  const owner = auth.status === 'authenticated' && !isDemoGuest(auth.user);

  // The request goes as soon as the page knows it may send it.
  useEffect(() => {
    if (!ready || owner || phase.kind !== 'starting') return;
    void start(attempt);
  }, [ready, owner, phase.kind, attempt, start]);

  function tryAgain() {
    setAttempt((n) => n + 1);
    setPhase({ kind: 'starting' });
  }

  // A signed-in owner already has boats. The guest session cookies would
  // replace theirs.
  if (owner) return <Navigate to="/dashboard" replace />;

  return (
    <section className="auth-panel">
      <div className="auth-card auth-card-v2 try-it" data-testid="try-it">
        {phase.kind === 'ended' ? (
          <>
            <h2>Your demo copy ended</h2>
            <p className="auth-sub" data-testid="try-it-ended">
              {DEMO_ENDED_NOTE}
            </p>
            <button type="button" className="tb-btn" onClick={tryAgain}>
              Get a new demo boat
            </button>
          </>
        ) : phase.kind === 'failed' ? (
          <>
            <h2>No demo boat yet</h2>
            <p className="auth-sub" role="alert" data-testid="try-it-error">
              {phase.failure.message}
            </p>
            <div className="try-it__actions">
              {phase.failure.retry ? (
                <button type="button" className="tb-btn" onClick={tryAgain}>
                  Try again
                </button>
              ) : null}
              {phase.failure.link === undefined ? (
                <Link to="/" className="tb-btn tb-btn--gray">
                  Back to the home page
                </Link>
              ) : (
                <Link to={phase.failure.link.to} className="tb-btn" data-testid="try-it-link">
                  {phase.failure.link.label}
                </Link>
              )}
            </div>
          </>
        ) : (
          <>
            <h2>Getting your demo boat ready</h2>
            <p className="auth-sub" aria-live="polite">
              You get your own copy of a demo boat for a few hours. No account is needed.
            </p>
            {configError ? (
              <p className="auth-sub" role="alert">
                The page could not load its settings.{' '}
                <button type="button" className="try-it__link" onClick={reloadConfig}>
                  Retry
                </button>
              </p>
            ) : (
              <p className="try-it__progress" aria-busy="true">
                Starting…
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
